import type { App } from 'vue'
import type { NavigationGuard, RouteLocation, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import type { Locale } from '@/settings/langs.ts'
import type { Token } from '@/stores/game'
import * as Sentry from '@sentry/vue'
import { useHead } from '@vueuse/head'
import { AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import { jwtDecode } from 'jwt-decode'
import pkgLodash from 'lodash'
import { createGtag, pageview } from 'vue-gtag'
import {
  createRouter,
  createWebHistory,
  START_LOCATION

} from 'vue-router'
import {
  closeLoginDialog,
  decodeGoatToken,
  getLoginGaContext,
  loginRequired
} from '@/components/layout/LoginDialog/LoginDialog'
import { gtagReady } from '@/components/layout/ManageCookies/ManageCookiesCompose'
import { loadingPage } from '@/components/layout/PageLoader/PageLoaderCompose'
import { gamesUtils } from '@/compositions/gamesUtils.ts'
import { setI18nLocale, spaI18n } from '@/compositions/i18nPlugin'
import { securedQueryParams } from '@/compositions/onMountedApp'
import { utilsPlugin } from '@/plugins/utilsPlugin.ts'
import { bindParams, buildHreflangLinks, getLocalePath, getLocaleRouteName, getRouteLocale, isSupportedLocale } from '@/router/langRouteBuilder.ts'
import { getVueRouter, setVueRouter } from '@/router/routerInstance'
import { autoDetectLanguage, defaultLocale, localeToOgLocale, supportedLocales } from '@/settings/langs.ts'
import { GameKeyEnum } from '@/types/enums.ts'
import pagesRoutes from './routes'

const { merge } = pkgLodash

const MainLayout = () => import('@/layouts/MainLayout.vue')

const { gaLogin, gaPageViewParams } = gaUtils
const { gaLoginMethods } = gaUtils
const { toStringParams, cloneDeep } = tools

/**
 *  @description set basic meta data of page (meta tags);
 *  @field title - title and og:title;
 *  @field description - description and og:description
 *  @field image - og:image
 *  @field path - og:url and canonical
 *  @field templateVariables - default values of variables used by templates string
 *  @templateVariables templates strings can be used in fields; eg title: '{gameName} game - Gamesture store';
 *                    variables are passed from this field (@templateVariables) and merged with route params and queries
 *  @field bodyClass - body class for page
 *  @field locale - locale of page; if not set, default locale is used
 *  @field routerGroup - router group of page; if not set,
 */
export interface PageBasicData {
  title?: string
  description?: string
  image?: string
  path?: string
  templateVariables?: Record<string, unknown>
  bodyClass?: string
  locale?: string
  routerGroup?: string
  ssgSharedRoute?: boolean
}

/**
 * @field ssg - Template variables injected into the route path to generate a static (SSG) page variant via vite-ssg.
 *        name - name route (can be template strings)
 *        path - path route (can be template strings)
 *        image - og:image
 *        templateVariables - variables for template strings; merged with route templateVariables
 */
interface PageMetaData {
  meta: {
    pageData?: PageBasicData
    pageLazyData?: (routeParams: Record<string, string>, routeLocale: string) => PageBasicData
  }
  ssg?: Record<string, string>[]
}
export type RoutesData = RouteRecordRaw & PageMetaData

export type RouteWithPageMetaData = RouteLocationNormalized

const TEMPLATE_VARIABLE_RE = /\{\s*(\w+)\s*\}/g // eg {property_name}

export function replaceTemplateVariables(template: string, data: Record<string, unknown> | undefined) {
  if (data) {
    return template.replace(TEMPLATE_VARIABLE_RE, (_, token) => String(data[token] || `{${token}}`))
  }
  return template
}

export function isString(value: unknown): value is string {
  return typeof value === 'string'
}

const reactiveHeadData = {
  title: ref(''),
  description: ref(''),
  image: ref(''),
  path: ref(''),
  bodyClass: ref(''),
  locale: ref(defaultLocale as string),
  ogLocale: ref(''),
  hreflangLinks: ref<{ rel: string, hreflang: string, href: string }[]>([]),
  isInit: false
}

export function initReactivePageMetaData() {
  useHead(computed(() => ({
    title: reactiveHeadData.title.value,
    meta: [
      {
        property: 'og:title',
        content: reactiveHeadData.title.value
      },
      {
        name: 'description',
        content: reactiveHeadData.description.value
      },
      {
        property: 'og:description',
        content: reactiveHeadData.description.value
      },
      {
        property: 'og:url',
        content: reactiveHeadData.path.value
      },
      {
        property: 'og:image',
        content: reactiveHeadData.image.value
      },
      {
        property: 'og:image:width',
        content: '1200'
      },
      {
        property: 'og:image:height',
        content: '630'
      },
      {
        property: 'og:image:alt',
        content: reactiveHeadData.title.value
      },
      {
        property: 'og:locale',
        content: reactiveHeadData.ogLocale.value
      }
    ],
    link: [
      {
        rel: 'canonical',
        href: reactiveHeadData.path.value
      },
      ...reactiveHeadData.hreflangLinks.value
    ],
    htmlAttrs: {
      lang: reactiveHeadData.locale.value
    },
    bodyAttrs: {
      class: reactiveHeadData.bodyClass.value
    }
  })))
}
export function setPageMetaData(data: PageBasicData, variables: Record<string, unknown> | undefined = undefined) {
  if (isString(data.title)) {
    reactiveHeadData.title.value = replaceTemplateVariables(data.title, variables)
  }
  if (isString(data.description)) {
    reactiveHeadData.description.value = replaceTemplateVariables(data.description, variables)
  }
  if (isString(data.path)) {
    reactiveHeadData.path.value = `${import.meta.env.VITE_HOST || ''}${data.path}`
  }
  if (isString(data.image)) {
    reactiveHeadData.image.value = data.image.startsWith('http')
      ? data.image
      : `${import.meta.env.VITE_HOST || ''}${data.image}`
  }
  if (isString(data.bodyClass)) {
    reactiveHeadData.bodyClass.value = data.bodyClass
  }
}

export function updateCurrentRoutePageMetaData(currentRoute: RouteWithPageMetaData, variables: Record<string, string> = {}) {
  const routeLocale = getRouteLocale(currentRoute)
  const metaLazyData: PageBasicData = currentRoute?.meta?.pageLazyData?.(toStringParams(currentRoute.params), routeLocale) || {}
  setPageMetaData({ ...(currentRoute?.meta?.pageData || {}), ...metaLazyData, templateVariables: undefined }, {
    ...(currentRoute?.meta?.pageData?.templateVariables),
    ...(currentRoute?.params || {}),
    ...(metaLazyData?.templateVariables || {}),
    ...variables
  })
  const routerGroup = currentRoute?.meta?.pageData?.routerGroup || ''
  reactiveHeadData.locale.value = routeLocale
  reactiveHeadData.ogLocale.value = localeToOgLocale[routeLocale as Locale] ?? routeLocale
  reactiveHeadData.hreflangLinks.value = routerGroup
    ? buildHreflangLinks(routerGroup, toStringParams(currentRoute.params))
    : []
}

let vue: App
async function guardBeforeEnter(to: RouteLocation) {
  if (vue) {
    const isDeepLinkToken = !!to?.query?.token?.length && !!to?.query?.gameKey
    const isDeepLinkUrl = to.fullPath.substring(0, 11) === '/deep-link/'
    if (isDeepLinkUrl && to?.query?.login === 'false') {
      const gameKey: Games.GameKeyLax = to?.query?.gameKey as Games.GameKeyType
      const gameSettings = gamesUtils.getGameSettings(gameKey)
      const backPath = to?.query?.route ? decodeURIComponent(String(to?.query?.route)) : ''
      setTimeout(() => {
        ElMessage({
          message: spaI18n.global.t('routerPlugin.deepLinkAutoOpenFailed'),
          type: 'warning',
          customClass: 'store-warning',
          offset: 100,
          duration: 0,
          showClose: true
        })
      }, 500)
      if (backPath) {
        return backPath
      }
      return getLocalePath('game-page', { gameID: gameSettings?.id })
    } else if (isDeepLinkUrl || isDeepLinkToken) {
      const gameKey: Games.GameKeyLax = to?.query?.gameKey as Games.GameKeyType
      gamesUtils.setGameToken(gameKey, null)
      const rawBuyOffer = localStorage?.getItem?.('buyOffer')
      const buyOffer = (rawBuyOffer ? JSON.parse(rawBuyOffer) : null) || {}
      const query = { ...to.query }
      // http://localhost:8100/game/dev-slash-and-roll?route=game-page&gameKey=slashAndRoll&offerId=offer211&token=26db5fe8633a25eb4abc36e2fc005967
      // https://gamesture-store-dev.vercel.app/app-deep-link/game/dev-soccer-clash?route=game-page&gameKey=soccerClash&offerId=&token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Ijg0NTQiLCJuYW1lIjoiUGxheWVyODQ1NG80MSIsInZhbGlkIjoiMTcwODA4MzQ1NiJ9.mGocs0VBJmsDeP_PWPjZEYVuqYe5wgFnNhI6mN0_zWU
      buyOffer.deepLink = true
      if (isDeepLinkToken) {
        let decodedJwt = null
        try {
          decodedJwt = jwtDecode(String(to.query.token))
        } catch (err) {
          // no token in the log - console output ends up in Sentry breadcrumbs
          console?.error?.('not decoded jwt', err)
        }
        const tokenData = {
          ...{ id: '', name: '', token: '', valid: '', __token: '' },
          token: decodedJwt ? String(to?.query?.token) : '',
          ...(decodedJwt || {}),
          __token: String(to?.query?.token)
        }
        delete query.token
        const gameSettings = gamesUtils.getGameSettings(gameKey)
        if (gameSettings?.gameKey) {
          const isConnectedGoatAccount = false
          if (tokenData.id) {
            // isConnectedGoatAccount = await autoLoginGoatFromGameAccount(tokenData.__token)
            if (!isConnectedGoatAccount) {
              gamesUtils.setGameToken(gameKey, tokenData as Token)
            }
          } else {
            gamesUtils.setGameToken(gameKey, tokenData as Token)
          }
          if (tokenData.token || isConnectedGoatAccount) {
            const gaParams = {
              gs_game: gameKey,
              gs_route: String(to?.query?.route || ''),
              ...getLoginGaContext(),
              method: `${gaLoginMethods.deepLink}${isConnectedGoatAccount ? `,${gaLoginMethods.goat}` : ''}`
            }
            setTimeout(() => {
              gaLogin(gaParams)
              closeLoginDialog()
            }, 1500)
            if (!query?.offerId) {
              setTimeout(() => {
                ElMessage({
                  message: spaI18n.global.t('loginBox.loggedIn'),
                  type: 'success',
                  customClass: 'left-auto right-10',
                  duration: 3000
                })
              }, 500)
            }
          }
        }
      }
      if (!buyOffer?.gameKey || buyOffer?.gameKey !== query?.gameKey || buyOffer?.offer?.id !== query?.offerId) {
        buyOffer.gameKey = query?.gameKey
        buyOffer.offer = null
      }

      localStorage?.setItem?.('buyOffer', JSON.stringify(buyOffer))

      const deleteKeys = ['route', 'gameKey', 'offerId', 'login']
      deleteKeys.forEach((key) => {
        if (key in query) {
          delete query[key]
        }
      })

      const backPath = to?.query?.route ? decodeURIComponent(String(to?.query?.route)) : ''
      if (backPath) {
        return { path: backPath, query: { ...query } }
      }
      if (gamesUtils?.list?.[gameKey]?.id) {
        if (to?.query?.offerId) {
          return {
            name: getLocaleRouteName('game-product-page'),
            params: { gameID: gamesUtils?.list?.[gameKey]?.id, offerID: to?.query?.offerId || '' },
            query: { ...query }
          }
        }
        return {
          name: getLocaleRouteName('game-page'),
          params: { gameID: gamesUtils?.list?.[gameKey]?.id },
          query: { ...query }
        }
      }
      return {
        name: getLocaleRouteName('home-page'),
        query: { ...query }
      }
    }
    // https://store.gamesture.com/game/the-gang?goat_token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjb20iOmZhbHNlLCJnYW1lcyI6eyJnYW5nIjoiNTE0OTg3MyJ9LCJpZCI6Ijc4Iiwic3RhbXAiOiIxNzQxNjg2MDU4IiwidmFsaWQiOiIxNzU0MjE4MzQ1In0.2RW-5nmsw4yHeF126-Y1OSJ68W_q2Ztl0a9cy1xjejk&goat_email=leszeklato@gamesture.com
    if (to.query?.goat_token && to?.query?.goat_email) {
      const query = { ...to.query }
      const token = query.goat_token
      const email = query.goat_email
      delete query.goat_token
      delete query.goat_email
      decodeGoatToken(String(token), String(email))
      const gaParams = {
        method: gaLoginMethods.goatLink,
        gs_game: gamesUtils.listById?.[String(to?.params?.gameID)]?.gameKey || '',
        gs_route: String(to?.name || ''),
        gs_section: gaUtils.gaSections.gaotPage
      }
      setTimeout(() => {
        gaLogin(gaParams)
      }, 1500)
      return { path: to?.path, query: { ...query } }
    }
    // temporary_token
    if (to.query?.temporary_token && to?.params?.gameID) {
      const query = { ...to.query }
      const temporary_token = query.temporary_token
      const gameKey = gamesUtils.listById?.[String(to?.params?.gameID)]?.gameKey || ''
      if (gameKey) {
        gamesUtils.clearGameTokens()
        await gamesUtils.apiGet(GameKeyEnum.goat, gamesUtils.getGoatApiUrl('handle-temporary-token'), { temporary_token }, false).then((response) => {
          if (response?.data?.status === 'ok') {
            if (response?.data?.store_token) {
              let decodedJwt = null
              try {
                decodedJwt = jwtDecode(response.data.store_token)
              } catch (err) {
                console?.error?.('not decoded jwt', err) // no token in the log - console output ends up in Sentry breadcrumbs
              }
              const tokenData = {
                ...{ id: '', name: '', token: '', valid: '', __token: '' },
                ...{
                  token: decodedJwt ? response.data.store_token : '',
                  ...(decodedJwt || {}),
                  __token: response.data.store_token
                }
              }
              if (tokenData.id) {
                gamesUtils.setGameToken(gameKey, tokenData as Token)
                const gaParams = {
                  method: gaLoginMethods.temporaryLink,
                  gs_game: gameKey,
                  gs_route: String(to?.name || ''),
                  gs_section: gaUtils.gaSections.pageOffer
                }
                setTimeout(() => {
                  gaLogin(gaParams)
                }, 1500)
              } else {
                throw new Error('Not valid')
              }
            } else if (response?.data?.goat_token && response?.data?.email) {
              const decodedToken = decodeGoatToken(response.data.goat_token, response.data.email)
              if (decodedToken?.token?.length) {
                const gaParams = {
                  method: gaLoginMethods.temporaryLink,
                  gs_game: gameKey,
                  gs_route: String(to?.name || ''),
                  gs_section: gaUtils.gaSections.pageOffer
                }
                setTimeout(() => {
                  gaLogin(gaParams)
                }, 1500)
              } else {
                throw new Error('Not valid')
              }
            } else {
              throw new Error('Not valid')
            }
          } else {
            throw new Error('Not valid')
          }
        }).catch((err) => {
          if (((err?.response?.status === 401 || err?.status === 401) && err instanceof AxiosError) || err?.message === 'Not valid') {
            loginRequired.value = true
            utilsPlugin.getApp()?.$notify?.closeAll()
            utilsPlugin.getApp()?.$notify({
              title: spaI18n.global.t('routerPlugin.tokenLinkExpiredTitle'),
              type: 'warning',
              customClass: 'child-inherit-colors text-orange-600 z-999999',
              message: spaI18n.global.t('routerPlugin.tokenLinkExpiredMessage'),
              duration: 18000
            })
            gamesUtils.showLogInDialog(true, {})
          } else {
            gamesUtils.catchError(err)
          }
        })
      }
      delete query.temporary_token
      return { path: to?.path, query: { ...query } }
    }
    // securedQueryParams
    if (to.query?.r_p?.length) {
      const redirectParams = `${to.query?.r_p}`?.split(',')
      securedQueryParams.value.r_p_gameKey = redirectParams?.[0] || ''
      securedQueryParams.value.r_p_moGroupId = redirectParams?.[1] || ''
      return { path: to?.path, query: { ...to.query, r_p: undefined, purchaseId: undefined, checkoutId: undefined, purchaseToken: undefined } }
    }

    return true
  }
  return true
}
function getSkeletonRoutes(): RouteRecordRaw[] {
  return [
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: () => import('@/pages/404/ErrorPage.vue'),
      beforeEnter: [guardBeforeEnter as NavigationGuard]
    },
    {
      path: '/',
      name: 'home',
      component: MainLayout,
      beforeEnter: [guardBeforeEnter as NavigationGuard],
      children: []
    }
  ]
}

function routerSetPageMetaDataCallBack(to: RouteLocationNormalized) {
  const data: PageMetaData = to
  if (!reactiveHeadData.isInit) {
    initReactivePageMetaData()
    reactiveHeadData.isInit = true
  }
  const resetData: PageBasicData = {
    title: '',
    description: '',
    image: '',
    path: data.meta?.pageData?.path || to.path || '',
    bodyClass: ''
  }
  setPageMetaData(
    { ...resetData, ...data.meta.pageData },
    { ...data.meta?.pageData?.templateVariables, ...to.query, ...to.params }
  )
  updateCurrentRoutePageMetaData(to)
}

export function routerSsgSetPageMetaDataCallBack(to: RouteLocationNormalized) {
  const data: PageMetaData = to
  const ssgSharedRoute = data.meta.pageData?.ssgSharedRoute ?? false
  const locale = data.meta.pageData?.locale || defaultLocale
  const routerGroup = data.meta.pageData?.routerGroup || ''
  const ogLocale = localeToOgLocale[locale as Locale] ?? locale
  const hreflangLinks = !ssgSharedRoute && routerGroup
    ? buildHreflangLinks(routerGroup, data.meta.pageData?.templateVariables)
    : []
  useHead({
    title: replaceTemplateVariables(data.meta.pageData?.title ?? '', data.meta.pageData?.templateVariables ?? {}),
    meta: [
      {
        property: 'og:title',
        content: replaceTemplateVariables(data.meta.pageData?.title ?? '', data.meta.pageData?.templateVariables ?? {})
      },
      {
        name: 'description',
        content: replaceTemplateVariables(data.meta.pageData?.description ?? '', data.meta.pageData?.templateVariables ?? {})
      },
      {
        property: 'og:description',
        content: replaceTemplateVariables(data.meta.pageData?.description ?? '', data.meta.pageData?.templateVariables ?? {})
      },
      ...(!ssgSharedRoute ? [{ property: 'og:url', content: `${import.meta.env.VITE_HOST || ''}${to.path}` }] : []),
      {
        property: 'og:image',
        content: data.meta.pageData?.image
          ? data.meta.pageData.image.startsWith('http')
            ? data.meta.pageData.image
            : `${import.meta.env.VITE_HOST || ''}${data.meta.pageData.image}`
          : ''
      },
      {
        property: 'og:image:width',
        content: '1200'
      },
      {
        property: 'og:image:height',
        content: '630'
      },
      {
        property: 'og:image:type',
        content: 'image/webp'
      },
      {
        property: 'og:image:alt',
        content: replaceTemplateVariables(data.meta.pageData?.title ?? '', data.meta.pageData?.templateVariables ?? {})
      },
      {
        property: 'og:locale',
        content: ogLocale
      }
    ],
    link: [
      ...(!ssgSharedRoute ? [{ rel: 'canonical', href: `${import.meta.env.VITE_HOST || ''}${to.path}` }] : []),
      ...hreflangLinks
    ],
    htmlAttrs: {
      lang: locale
    },
    bodyAttrs: {
      class: data.meta?.pageData?.bodyClass ?? ''
    }
  })
}

// Dedup guard shared between the afterEach-triggered call and the consent-ready catch-up
// (see the `watch` in initRouter() below) — both can legitimately fire for the same page
// (e.g. consent finishes resolving just as/after a navigation completes), and without this
// they'd double-send the same page_view/campaign hit to GA. Keyed on path+search so a later,
// genuinely different navigation still tracks normally.
let lastGaPageviewTrack = ''
// The only page_view sender (one per page): gaEvent() no longer sends page_view - it used to, so every
// SPA navigation produced two page_views from this file (plus GA4 enhanced measurement's history-change
// page_view, which must stay disabled in the GA4 web stream settings).
function trackGaPageview(route: RouteLocationNormalized) {
  const pagePath = window?.location?.pathname ?? ''
  const trackKey = `${pagePath}${window?.location?.search ?? ''}`
  if (trackKey === lastGaPageviewTrack)
    return
  lastGaPageviewTrack = trackKey
  pageview({
    ...gaPageViewParams({
      // reactiveHeadData.title (not document.title) — @vueuse/head's useHead() updates the DOM
      // <title> reactively, on a later tick than the synchronous ref assignment in
      // routerSetPageMetaDataCallBack(), so document.title here would still read the PREVIOUS
      // page's title (confirmed reproducible independent of any other routing change, 2026-08-04).
      page_title: reactiveHeadData.title.value || document?.title || '',
      page_location: window?.location?.href ?? '',
      page_path: pagePath,
      // same params as every gaEvent (gs_client_id, gs_user_id, gs_launch_mode)
      gs_game: gamesUtils.listById?.[String(route.params.gameID)]?.gameKey
    }),
    send_page_view: false
  })
}

function initRouter() {
  const skeletonRoutes = getSkeletonRoutes()
  if (skeletonRoutes[1]) {
    skeletonRoutes[1].children = (pagesRoutes() || [])
  }
  // console.log('skeletonRoutes', skeletonRoutes?.[1]?.children)
  skeletonRoutes.push(
    ...[
      {
        path: '/gamesture-terms-of-service.pdf',
        beforeEnter: () => {
          window.location.href = 'https://gamesture.com/terms-of-service'
        },
        component: MainLayout
      },
      {
        path: '/gamesture-terms-of-service.html',
        beforeEnter: () => {
          window.location.href = 'https://gamesture.com/terms-of-service'
        },
        component: MainLayout
      },
      {
        path: '/gamesture-privacy-policy.pdf',
        beforeEnter: () => {
          window.location.href = 'https://gamesture.com/privacy-policy'
        },
        component: MainLayout
      },
      {
        path: '/gamesture-privacy-policy.html',
        beforeEnter: () => {
          window.location.href = 'https://gamesture.com/privacy-policy'
        },
        component: MainLayout
      }
    ]
  )
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: skeletonRoutes,
    scrollBehavior(to) {
      if (to.hash) {
        return false
      }
      return { top: 0, behavior: 'smooth' }
    }
  })

  /**
   * Locale redirect: runs before any component mounts to avoid flash of wrong locale.
   */
  router.beforeEach((to) => {
    if (typeof window === 'undefined')
      return
    if (supportedLocales.length <= 1)
      return

    // flang: forced language — override preferred-locale and redirect
    const flangValue = to.query.flang
    if (flangValue !== undefined) {
      const { flang: _, ...queryWithoutFlang } = to.query
      if (typeof flangValue === 'string' && isSupportedLocale(flangValue)) {
        localStorage.setItem('preferred-locale', flangValue)
        const currentLoc = getRouteLocale(to)
        if (flangValue !== currentLoc) {
          const newPath = getLocalePath(to.meta.pageData?.routerGroup, to.params, flangValue)
          if (newPath && newPath !== to.path)
            return { path: newPath, query: queryWithoutFlang }
        }
      }
      return { path: to.path, query: queryWithoutFlang }
    }

    const currentLoc = getRouteLocale(to)

    // If the URL explicitly uses a non-default locale, respect it — don't redirect.
    // Only apply preference/auto-detect redirect when on the default (English) locale.
    if (currentLoc === defaultLocale) {
      const preferred = localStorage.getItem('preferred-locale')
      if (preferred && isSupportedLocale(preferred) && preferred !== currentLoc) {
        const newPath = getLocalePath(to.meta.pageData?.routerGroup, to.params, preferred)
        if (newPath && newPath !== to.path)
          return { path: newPath, query: to.query }
      }
      if (autoDetectLanguage && !preferred) {
        const detected = navigator.language.split('-')[0]
        if (isSupportedLocale(detected) && detected !== currentLoc) {
          const newPath = getLocalePath(to.meta.pageData?.routerGroup, to.params, detected)
          if (newPath && newPath !== to.path)
            return { path: newPath, query: to.query }
        }
      }
    }
  })

  /**
   * Offer-preview deep link: redirect a direct landing/reload on `?offerGameId=X&offerId=Y`
   * (the transient query shape OfferDialogCompose's showOfferUrl() uses while the preview
   * dialog is open) to the canonical /offer/:offerID route. Only on the very first navigation
   * (from === START_LOCATION) — matching unconditionally on every navigation would also
   * intercept and break the dialog's own query-param navigation used to open the preview.
   * Previously done as a router.push() from OfferDialogComponent.vue's mount, which let the
   * transient URL's own afterEach page_view (trackGaPageview()) fire before the redirect — double-counting
   * page_view for what is really one page load (reported 2026-08-04).
   */
  router.beforeEach((to, from) => {
    if (from === START_LOCATION && to.query?.offerGameId && to.query?.offerId) {
      return getLocalePath('game-product-page', {
        gameID: String(to.query.offerGameId),
        offerID: String(to.query.offerId)
      })
    }
  })

  /**
   * Sync i18n locale with the target route locale before beforeEnter guards run.
   * This ensures spaI18n.global.t() returns the correct translation in guardBeforeEnter.
   */
  router.beforeEach((to) => {
    if (typeof window === 'undefined')
      return
    setI18nLocale(getRouteLocale(to) as Locale)
  })

  /**
   *  start loaderPage
   */
  router.beforeEach((to, from) => {
    if (from.path && to.path !== from.path) {
      loadingPage.value = true
    }
  })

  /**
   * Register seo, end finish loaderPage
   */
  router.afterEach((to) => {
    loadingPage.value = false
    routerSetPageMetaDataCallBack(to)
    setI18nLocale(getRouteLocale(to) as Locale)
    // Replaces vue-gtag's own `pageTracker: { router }` auto-tracking (removed below) — that
    // mechanism relies on an internal, unawaited `router.isReady()` call that becomes an
    // unhandled promise rejection whenever the initial navigation fails (see GAMESTURE-STORE-B1/
    // B0/AT/AF family in Sentry). `trackGaPageview()` does the same UTM-param → GA `campaign`
    // extraction synchronously, with no dependency on isReady(). Gated behind `siteGtag()` (same
    // consent check gaEvent() uses) since, unlike the removed pageTracker, this call isn't
    // otherwise gated by addGtag() having been consented to — calling it unconditionally would
    // send data to GA regardless of cookie consent.
    //
    // On the very first navigation of a fresh load, `addGtag()`'s script hasn't finished loading
    // yet at this point even when consent was already granted in a prior session — siteGtag()
    // is still false here — so this specific call is skipped and the initial pageview/campaign
    // hit would otherwise be silently lost. The `watch` below catches that up once consent
    // actually becomes ready; the shared trackGaPageview() dedup guard stops the two from
    // double-sending if they end up firing for the same page.
    //
    // No VITE_DEBUG_GA bypass: with VITE_DEBUG_GA='on' gtag.js is loaded in dev mode too, and a
    // pageview() called before consent would be queued in dataLayer and sent once consent loads it.
    if (utilsPlugin.siteGtag()) {
      trackGaPageview(to)
    }
  })
  // Catch-up for the gap above: fires once, the moment consent (and the gtag.js script load
  // it triggers) finishes resolving — covers the initial pageview/campaign hit that the very
  // first afterEach navigation missed because siteGtag() wasn't ready yet at that point.
  watch(() => Boolean(utilsPlugin.siteGtag()), (isReady, wasReady) => {
    if (isReady && !wasReady) {
      trackGaPageview(router.currentRoute.value)
    }
  })
  /**
   * Register deploy-version checker on router error when SSG;
   * (preventing failures due to the implementation of a new version)
   */
  router.onError((_, to) => {
    const isSsr = import.meta.env.SSR
    const isDevMode = import.meta.env.MODE === 'development'
    if (!isSsr && !isDevMode) {
      let deployVersion = localStorage?.getItem?.('deploy-version')
      if (deployVersion && deployVersion?.length > 50) {
        deployVersion = ''
        localStorage.setItem?.('deploy-version', deployVersion)
      }
      fetch('/build.txt')
        .then((response) => {
          if (response.ok) {
            return response.text()
          } else {
            Sentry.captureException(new Error('Failed to fetch data (extra response)'), {
              extra: {
                response,
                msg: 'in router.onError'
              }
            })
          }
        })
        .then((buildTxt) => {
          if (buildTxt && buildTxt?.length <= 50) {
            if (buildTxt !== deployVersion) {
              localStorage?.setItem?.('deploy-version', buildTxt)
              if (to?.fullPath && window?.location) {
                const connector = Object.keys(to.query).length > 0 ? '&' : '?'
                window.location.href = `${to.fullPath}${connector}__nc=${Date.now()}`
              } else {
                location?.reload?.()
              }
            }
          } else {
            if (Sentry?.getClient()) {
              Sentry.captureException(new Error('not valid buildTxt'), {
                extra: {
                  to: to?.fullPath || window.location.href || location?.href,
                  buildTxt: buildTxt?.length ? buildTxt?.substring(0, 50) : buildTxt,
                  msg: 'not valid buildTxt in router.onError'
                }
              })
            }
          }
        })
        .catch((error) => {
          if (Sentry?.getClient()) {
            Sentry.captureException(error, {
              extra: {
                msg: 'catch error in router.onError'
              }
            })
          }
        })
    }
  })
  vue.use(router)
  setVueRouter(router)
  const gtagId = import.meta.env.VITE_PROD ? 'G-ZCVJDJHNK2' : 'G-MNJS9M8EJW'
  const gtag = createGtag({
    tagId: gtagId,
    appName: 'Store - Gamesture',
    config: {
      id: gtagId,
      send_page_view: false,
      // config-level params are attached by GA4 to every hit (incl. router page_view via vue-gtag pageview()) - gtag('set') is not
      ...gaUtils.gaConfigParams()
    },
    hooks: {
      // gtagReady is only set true here — not in addGtag().then() (see ManageCookiesComponent.vue) —
      // because vue-gtag's addGtag() resolves successfully even when the script fails to load
      // (its internal try/catch swallows script:error without rethrowing). This hook fires only
      // on a genuine script onload, so it's the actual source of truth for "gtag is really usable".
      'script:loaded': () => {
        gtagReady.value = true
        gaUtils.onReadyVueGtag(gtagId)
      },
      // Diagnostic only — addGtag() already catches its own script-load rejection internally
      // (see the vue-gtag source: it's wrapped in try/catch around the dynamic script inject),
      // so this hook fires for the same failure that reaches that catch. Reported here too so a
      // recurrence of the GAMESTURE-STORE-B1/B0/AT/B2 cryptic-error family can be checked against
      // this tag to rule in/out addGtag()'s own script-loading path as the source. Wrapped in a
      // real Error (not the raw DOM Event) so Sentry gets a readable title/grouping, and the
      // failing script's own src is extracted from the event target — the raw event only ever
      // serializes to opaque HTMLElement labels, never the actual URL that failed.
      'script:error': (err: unknown) => {
        const target = (err as Event)?.target as HTMLScriptElement | undefined
        const failedScriptSrc = target?.src || 'unknown'
        Sentry.captureException(new Error(`vue-gtag script failed to load: ${failedScriptSrc}`), {
          tags: { source: 'gtagScriptError' },
          extra: {
            failedScriptSrc,
            online: navigator.onLine,
            connectionType: (navigator as any)?.connection?.effectiveType ?? 'unknown'
          }
        })
      }
    },
    initMode: 'manual'
  })
  vue.use(gtag)
}

export function initSsgRoutes() {
  const skeletonRoutes = []
  const routes = getSkeletonRoutes()?.[1]
  if (routes) {
    skeletonRoutes.push(routes)
  }
  if (skeletonRoutes[0]) {
    skeletonRoutes[0].children = pagesRoutes().flatMap((routeData) => {
      const ssgRoutesData: RoutesData[] = []
      if (routeData.ssg?.length) {
        routeData.ssg.forEach((ssgParams) => {
          const renderedRouteData: RoutesData = cloneDeep(routeData)
          const routeLocale = renderedRouteData.meta?.pageData?.locale || defaultLocale
          const metaLazyData: PageBasicData = renderedRouteData?.meta?.pageLazyData?.(ssgParams, routeLocale) || {}
          renderedRouteData.meta.pageData = merge({}, renderedRouteData?.meta?.pageData || {}, metaLazyData)
          const templateVariables = { ...ssgParams, ...(renderedRouteData.meta.pageData.templateVariables || {}), locale: routeLocale }
          renderedRouteData.path = bindParams(renderedRouteData.path, templateVariables)
          renderedRouteData.name = renderedRouteData.path
          if (renderedRouteData.meta.pageData) {
            renderedRouteData.meta.pageData.title = replaceTemplateVariables(renderedRouteData.meta.pageData.title ?? '', templateVariables)
            renderedRouteData.meta.pageData.description = replaceTemplateVariables(renderedRouteData.meta.pageData.description ?? '', templateVariables)
            renderedRouteData.meta.pageData.templateVariables = templateVariables
          }
          if (renderedRouteData?.ssg) {
            delete renderedRouteData.ssg
          }
          ssgRoutesData.push(renderedRouteData)
        })

        return ssgRoutesData
      } else {
        routeData.path = bindParams(routeData.path, routeData.meta?.pageData?.templateVariables)
        routeData.name = routeData.path
      }
      return routeData
    })
  }
  return skeletonRoutes
}
export default {
  install: (app: App) => {
    vue = app
    initRouter()
  },
  getVueRouter() {
    return getVueRouter()
  }
}

export { getVueRouter }
